import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardTitle, 
  Form, 
  FormGroup, 
  Label, 
  Input,
  Button,
  Carousel,
  CarouselItem,
  CarouselControl,
  Alert
} from "reactstrap"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { resolveImage } from "../../helpers/config"
import { withTranslation } from "react-i18next"
import { getPropById, enableprop0, enableprop, disableprop0, disableprop, updateowner, updatepassword } from "../../helpers/requests"
import { Oval } from "react-loader-spinner"
import Swal from 'sweetalert2'

const PropertyDetail = (props) => {
  const [property, setProperty] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isEnabling, setIsEnabling] = useState(false)
  const [isDisabling, setIsDisabling] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const { id } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    document.title = "Détails de la Propriété"
    
    const fetchPropertyDetails = async () => {
      try {
        const data = await getPropById(id)
        setProperty(data)
        setIsLoading(false)
      } catch (error) {
        console.error("Error fetching property details:", error)
        setIsLoading(false)
        window.location.href ='/properties'
      }
    }

    fetchPropertyDetails()
  }, [id, navigate])

  const handleBack = () => {
    navigate('/proprety')
  }

  const next = () => {
    if (property.images.length > 1) {
      const nextIndex = activeIndex === property.images.length - 1 ? 0 : activeIndex + 1
      setActiveIndex(nextIndex)
    }
  }

  const previous = () => {
    if (property.images.length > 1) {
      const nextIndex = activeIndex === 0 ? property.images.length - 1 : activeIndex - 1
      setActiveIndex(nextIndex)
    }
  }

  const handleEnableProperty = async () => {
    setIsEnabling(true)
    setErrorMessage(null)
    try {
      await enableprop0(id)
      try { await enableprop(id) } catch {}
      setProperty(prev => ({ ...prev, available: true }))
    } catch (error) {
      console.error("Error enabling property:", error)
      setErrorMessage(props.t("Erreur lors de l'activation de la propriété"))
    } finally {
      setIsEnabling(false)
    }
  }

  const handleDisableProperty = async () => {
    setIsDisabling(true)
    setErrorMessage(null)
    try {
      await disableprop0(id)
      try { await disableprop(id) } catch {}
      setProperty(prev => ({ ...prev, available: false }))
    } catch (error) {
      console.error("Error disabling property:", error)
      setErrorMessage(props.t("Erreur lors de la désactivation de la propriété"))
    } finally {
      setIsDisabling(false)
    }
  }

  const handleChangeOwner = async () => {
    const { value: newOwner } = await Swal.fire({
      title: 'Changer le propriétaire',
      input: 'text',
      inputLabel: 'Nouveau propriétaire',
      inputPlaceholder: 'Entrez le nouveau propriétaire',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'Vous devez entrer un nouveau propriétaire!'
        }
      }
    })

    if (newOwner) {
      try {
        await updateowner(id, newOwner)
        const updatedProperty = await getPropById(id)
        setProperty(updatedProperty)
        Swal.fire('Succès!', 'Le propriétaire a été changé avec succès.', 'success')
      } catch (error) {
        console.error("Error changing owner:", error)
        Swal.fire('Erreur!', 'Erreur lors du changement du propriétaire.', 'error')
      }
    }
  }

  const handleChangePassword = async () => {
    const { value: newPassword } = await Swal.fire({
      title: 'Changer le mot de passe',
      input: 'password',
      inputLabel: 'Nouveau mot de passe',
      inputPlaceholder: 'Entrez le nouveau mot de passe',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'Vous devez entrer un nouveau mot de passe!'
        }
      }
    })

    if (newPassword) {
      try {
        await updatepassword(id, newPassword)
        Swal.fire('Succès!', 'Le mot de passe a été changé avec succès.', 'success')
      } catch (error) {
        console.error("Error changing password:", error)
        Swal.fire('Erreur!', 'Erreur lors du changement du mot de passe.', 'error')
      }
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Oval height={80} width={80} color="#556ee6" visible={true} />
      </div>
    )
  }

  if (!property) {
    return <div>Propriété non trouvée</div>
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs 
            title={props.t("Détails de la Propriété")} 
            breadcrumbItem={props.t("Détails")} 
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">{props.t("Détails de la Propriété")}</CardTitle>
                  
                  {errorMessage && (
                    <Alert color="danger" className="mb-4">
                      {errorMessage}
                    </Alert>
                  )}

                  {property.images && property.images.length > 0 && (
                    <Row className="mb-4">
                      <Col md={12}>
                        <Carousel
                          activeIndex={activeIndex}
                          next={next}
                          previous={previous}
                        >
                          {property.images.map((image, index) => (
                            <CarouselItem
                              key={image.id}
                              onExiting={() => {}}
                              onExited={() => {}}
                            >
                              <img
                                src={resolveImage(image.url)}
                                alt={`Property ${index + 1}`}
                                className="img-fluid mx-auto d-block"
                                style={{ maxHeight: '400px', objectFit: 'cover' }}
                              />
                            </CarouselItem>
                          ))}
                          <CarouselControl
                            direction="prev"
                            directionText="Previous"
                            onClickHandler={previous}
                          />
                          <CarouselControl
                            direction="next"
                            directionText="Next"
                            onClickHandler={next}
                          />
                        </Carousel>
                      </Col>
                    </Row>
                  )}

                  <Form>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("ID")}</Label>
                          <Input 
                            type="text" 
                            value={property.id} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Propriétaire")}</Label>
                          <Input 
                            type="text" 
                            value={property.owner} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Code de Propriété")}</Label>
                          <Input 
                            type="text" 
                            value={property.code} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Type de Propriété")}</Label>
                          <Input 
                            type="text" 
                            value={property.type} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Nom")}</Label>
                          <Input 
                            type="text" 
                            value={property.name} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Téléphone")}</Label>
                          <Input
                            type="text"
                            value={property.phonenumber}
                            readOnly
                            disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("E-mail")}</Label>
                          <Input
                            type="email"
                            value={property.email || ""}
                            readOnly
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("État")}</Label>
                          <Input 
                            type="text" 
                            value={property.state} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Prix de Départ")}</Label>
                          <Input 
                            type="text" 
                            value={property.startprice} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{props.t("Localisation")}</Label>
                          <Input 
                            type="textarea" 
                            value={property.location} 
                            readOnly 
                            disabled 
                            rows={2}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Latitude")}</Label>
                          <Input 
                            type="text" 
                            value={property.latitude} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Longitude")}</Label>
                          <Input 
                            type="text" 
                            value={property.longitude} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>{props.t("Description")}</Label>
                          <Input 
                            type="textarea" 
                            value={property.description} 
                            readOnly 
                            disabled 
                            rows={6}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Services")}</Label>
                          <Input 
                            type="text" 
                            value={(() => { const s = property.services; if (Array.isArray(s)) return s.map(i => typeof i === 'object' ? i.name || JSON.stringify(i) : i).join(', '); if (typeof s === 'string') { try { const p = JSON.parse(s); if (Array.isArray(p)) return p.map(i => typeof i === 'object' ? i.name || JSON.stringify(i) : i).join(', '); } catch {} return s; } return ''; })()}
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>{props.t("Date de Création")}</Label>
                          <Input 
                            type="text" 
                            value={new Date(property.created_at).toLocaleString()} 
                            readOnly 
                            disabled 
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-3">
                      <Col>
                        <Button 
                          color="secondary" 
                          onClick={handleBack}
                          className="me-2"
                        >
                          {props.t("Retour")}
                        </Button>
                        <Button 
                          color="success" 
                          onClick={handleEnableProperty}
                          disabled={isEnabling || isDisabling || property.available}
                          className="me-2"
                        >
                          {isEnabling 
                            ? props.t("Activation en cours...") 
                            : props.t("Activer")
                          }
                        </Button>
                        <Button 
                          color="danger" 
                          onClick={handleDisableProperty}
                          className="me-2"
                          disabled={isEnabling || isDisabling || !property.available}
                        >
                          {isDisabling 
                            ? props.t("Désactivation en cours...") 
                            : props.t("Désactiver")
                          }
                        </Button>
                        <Button 
                          color="info" 
                          onClick={handleChangeOwner}
                          className="me-2"
                        >
                          {props.t("Changer le propriétaire")}
                        </Button>
                        <Button 
                          color="warning" 
                          onClick={handleChangePassword}
                        >
                          {props.t("Changer le mot de passe")}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

PropertyDetail.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(PropertyDetail)